import { useEffect, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import AdminLayout from "@/components/admin/AdminLayout";

type Coverage =
  | "connected"
  | "configured_awaiting_provider"
  | "manually_managed"
  | "manual"
  | "unavailable";

type Competition = {
  id: string;
  key: string;
  name: string;
  sport: string;
  season: string | null;
  coverage_status: Coverage;
  provider: string;
  provider_league_id: string | null;
  public_visible: boolean;
  sort_order: number;
  notes: string | null;
  last_sync_at: string | null;
  last_sync_status: string | null;
  last_sync_error: string | null;
  display_name: string | null;
  source_name: string | null;
  source_url: string | null;
  enabled_for_public: boolean;
  last_successful_sync_at: string | null;
  last_error: string | null;
};

type ScoreEvent = {
  id: string;
  competition_id: string;
  source: string;
  provider: string;
  provider_event_id: string | null;
  start_time: string;
  status: string;
  period: string | null;
  home_name: string;
  away_name: string;
  home_score: number | null;
  away_score: number | null;
  published: boolean;
  last_updated_at: string;
};

const PROVIDERS = ["none", "manual", "api_sports", "sportradar", "sportsdataio", "league_direct"];
const STATUSES = ["scheduled", "live", "final", "postponed", "cancelled"];

const coverageStyles: Record<Coverage, string> = {
  connected: "bg-primary/10 text-primary border-primary/30",
  configured_awaiting_provider: "bg-muted text-foreground border-border",
  manually_managed: "bg-secondary/10 text-secondary-foreground border-secondary/40",
  manual: "bg-secondary/10 text-secondary-foreground border-secondary/40",
  unavailable: "bg-muted text-muted-foreground border-border",
};

const emptyEvent = {
  competition_id: "",
  start_time: "",
  status: "scheduled",
  home_name: "",
  away_name: "",
  home_score: "",
  away_score: "",
  period: "",
  published: false,
};

const AdminScores = () => {
  const [comps, setComps] = useState<Competition[]>([]);
  const [events, setEvents] = useState<ScoreEvent[]>([]);
  const [draft, setDraft] = useState({ ...emptyEvent });
  const [loading, setLoading] = useState(true);

  const load = async () => {
    const [{ data: c, error: ce }, { data: e }] = await Promise.all([
      supabase.from("score_competitions").select("*").order("sort_order"),
      supabase.from("score_events").select("*").order("start_time", { ascending: false }).limit(100),
    ]);
    if (ce) toast.error(ce.message);
    setComps((c ?? []) as Competition[]);
    setEvents((e ?? []) as ScoreEvent[]);
    setLoading(false);
  };

  useEffect(() => {
    load();
  }, []);

  const updateComp = async (id: string, patch: Partial<Competition>) => {
    const { error } = await supabase.from("score_competitions").update(patch).eq("id", id);
    if (error) return toast.error(error.message);
    setComps((prev) => prev.map((c) => (c.id === id ? { ...c, ...patch } : c)));
  };

  const addEvent = async () => {
    if (!draft.competition_id || !draft.start_time || !draft.home_name || !draft.away_name) {
      toast.error("League, start time and both team names are required");
      return;
    }
    const { error } = await supabase.from("score_events").insert({
      competition_id: draft.competition_id,
      source: "manual",
      provider: "manual",
      start_time: new Date(draft.start_time).toISOString(),
      status: draft.status,
      period: draft.period || null,
      home_name: draft.home_name,
      away_name: draft.away_name,
      home_score: draft.home_score === "" ? null : Number(draft.home_score),
      away_score: draft.away_score === "" ? null : Number(draft.away_score),
      published: draft.published,
    });
    if (error) return toast.error(error.message);
    toast.success("Event added");
    setDraft({ ...emptyEvent });
    load();
  };

  const updateEvent = async (id: string, patch: Partial<ScoreEvent>) => {
    const { error } = await supabase.from("score_events").update(patch).eq("id", id);
    if (error) return toast.error(error.message);
    load();
  };

  const removeEvent = async (id: string) => {
    if (!confirm("Delete this event?")) return;
    const { error } = await supabase.from("score_events").delete().eq("id", id);
    if (error) return toast.error(error.message);
    load();
  };

  const compName = (id: string) => comps.find((c) => c.id === id)?.name ?? "—";
  const manualComps = comps.filter((c) => c.coverage_status !== "unavailable");

  return (
    <AdminLayout>
      <div className="space-y-10 max-w-full">
        <header>
          <h1 className="font-display text-3xl">Scores</h1>
          <p className="text-muted-foreground text-sm mt-1">
            Coverage per league, plus manually entered fixtures. Public pages show only published
            events from leagues marked visible.
          </p>
        </header>

        <section>
          <h2 className="text-sm font-semibold uppercase tracking-wider mb-3">Coverage matrix</h2>
          <div className="overflow-x-auto rounded-xl border border-border">
            <table className="w-full min-w-[860px] text-sm">
              <thead className="bg-muted/60 text-left">
                <tr>
                  <th className="p-3 font-semibold">League</th>
                  <th className="p-3 font-semibold">Sport</th>
                  <th className="p-3 font-semibold">Coverage</th>
                  <th className="p-3 font-semibold">Source</th>
                  <th className="p-3 font-semibold">League ID</th>
                  <th className="p-3 font-semibold">Public</th>
                  <th className="p-3 font-semibold">Last sync</th>
                </tr>
              </thead>
              <tbody>
                {comps.map((c) => (
                  <tr key={c.id} className="border-t border-border align-top">
                    <td className="p-3">
                      <div className="font-semibold">{c.name}</div>
                      {c.notes && (
                        <div className="text-xs text-muted-foreground max-w-[220px]">{c.notes}</div>
                      )}
                    </td>
                    <td className="p-3 text-muted-foreground">{c.sport}</td>
                    <td className="p-3">
                      <select
                        aria-label={`Coverage for ${c.name}`}
                        value={c.coverage_status}
                        onChange={(e) =>
                          updateComp(c.id, { coverage_status: e.target.value as Coverage })
                        }
                        className={`min-h-[44px] rounded-full border px-3 text-xs font-semibold ${coverageStyles[c.coverage_status]}`}
                      >
                        {c.coverage_status === "connected" && (
                          <option value="connected">Connected</option>
                        )}
                        <option value="configured_awaiting_provider">
                          Configured — awaiting provider
                        </option>
                        <option value="manually_managed">Manually managed</option>
                        <option value="unavailable">Unavailable</option>
                      </select>
                    </td>
                    <td className="p-3">
                      <select
                        aria-label={`Source for ${c.name}`}
                        value={c.provider}
                        onChange={(e) =>
                          updateComp(c.id, { provider: e.target.value, source_name: e.target.value })
                        }
                        className="min-h-[44px] rounded-lg border border-border bg-background px-2 text-xs"
                      >
                        {PROVIDERS.map((p) => (
                          <option key={p} value={p}>
                            {p}
                          </option>
                        ))}
                      </select>
                    </td>
                    <td className="p-3">
                      <input
                        aria-label={`Provider league ID for ${c.name}`}
                        defaultValue={c.provider_league_id ?? ""}
                        onBlur={(e) =>
                          updateComp(c.id, { provider_league_id: e.target.value || null })
                        }
                        className="min-h-[44px] w-24 rounded-lg border border-border bg-background px-2 text-xs"
                      />
                    </td>
                    <td className="p-3">
                      <label className="flex items-center gap-2 min-h-[44px]">
                        <input
                          type="checkbox"
                          checked={c.enabled_for_public}
                          onChange={(e) =>
                            updateComp(c.id, { enabled_for_public: e.target.checked })
                          }
                          className="w-4 h-4"
                        />
                        <span className="text-xs text-muted-foreground">Show</span>
                      </label>
                    </td>
                    <td className="p-3 text-xs text-muted-foreground">
                      {c.last_successful_sync_at
                        ? `${new Date(c.last_successful_sync_at).toLocaleString()} · ok`
                        : "No successful sync yet"}
                      {c.last_error && (
                        <div className="text-destructive mt-1 max-w-[200px]">{c.last_error}</div>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <p className="text-xs text-muted-foreground mt-2">
            Leagues marked Unavailable never appear in public filters, whatever the Show setting. "Connected" is set automatically once a provider sync succeeds.
          </p>
        </section>

        <section>
          <h2 className="text-sm font-semibold uppercase tracking-wider mb-3">Add a manual event</h2>
          <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4 rounded-xl border border-border p-4">
            <select
              aria-label="League"
              value={draft.competition_id}
              onChange={(e) => setDraft({ ...draft, competition_id: e.target.value })}
              className="min-h-[44px] rounded-lg border border-border bg-background px-3 text-sm"
            >
              <option value="">Select league…</option>
              {manualComps.map((c) => (
                <option key={c.id} value={c.id}>
                  {c.name}
                </option>
              ))}
            </select>
            <input
              aria-label="Start time"
              type="datetime-local"
              value={draft.start_time}
              onChange={(e) => setDraft({ ...draft, start_time: e.target.value })}
              className="min-h-[44px] rounded-lg border border-border bg-background px-3 text-sm"
            />
            <input
              aria-label="Away team"
              placeholder="Away team"
              value={draft.away_name}
              onChange={(e) => setDraft({ ...draft, away_name: e.target.value })}
              className="min-h-[44px] rounded-lg border border-border bg-background px-3 text-sm"
            />
            <input
              aria-label="Home team"
              placeholder="Home team"
              value={draft.home_name}
              onChange={(e) => setDraft({ ...draft, home_name: e.target.value })}
              className="min-h-[44px] rounded-lg border border-border bg-background px-3 text-sm"
            />
            <input
              aria-label="Away score"
              placeholder="Away score"
              inputMode="numeric"
              value={draft.away_score}
              onChange={(e) => setDraft({ ...draft, away_score: e.target.value })}
              className="min-h-[44px] rounded-lg border border-border bg-background px-3 text-sm"
            />
            <input
              aria-label="Home score"
              placeholder="Home score"
              inputMode="numeric"
              value={draft.home_score}
              onChange={(e) => setDraft({ ...draft, home_score: e.target.value })}
              className="min-h-[44px] rounded-lg border border-border bg-background px-3 text-sm"
            />
            <select
              aria-label="Status"
              value={draft.status}
              onChange={(e) => setDraft({ ...draft, status: e.target.value })}
              className="min-h-[44px] rounded-lg border border-border bg-background px-3 text-sm"
            >
              {STATUSES.map((s) => (
                <option key={s} value={s}>
                  {s}
                </option>
              ))}
            </select>
            <div className="flex items-center gap-3">
              <label className="flex items-center gap-2 text-sm min-h-[44px]">
                <input
                  type="checkbox"
                  checked={draft.published}
                  onChange={(e) => setDraft({ ...draft, published: e.target.checked })}
                  className="w-4 h-4"
                />
                Published
              </label>
              <button
                onClick={addEvent}
                className="min-h-[44px] px-5 rounded-full bg-primary text-primary-foreground text-sm font-semibold"
              >
                Add
              </button>
            </div>
          </div>
        </section>

        <section>
          <h2 className="text-sm font-semibold uppercase tracking-wider mb-3">Events</h2>
          {loading ? (
            <p className="text-muted-foreground text-sm">Loading…</p>
          ) : events.length === 0 ? (
            <p className="text-muted-foreground text-sm">No score events yet.</p>
          ) : (
            <div className="overflow-x-auto rounded-xl border border-border">
              <table className="w-full min-w-[820px] text-sm">
                <thead className="bg-muted/60 text-left">
                  <tr>
                    <th className="p-3 font-semibold">League</th>
                    <th className="p-3 font-semibold">Start</th>
                    <th className="p-3 font-semibold">Matchup</th>
                    <th className="p-3 font-semibold">Score</th>
                    <th className="p-3 font-semibold">Status</th>
                    <th className="p-3 font-semibold">Source</th>
                    <th className="p-3 font-semibold">Updated</th>
                    <th className="p-3 font-semibold">Public</th>
                    <th className="p-3" />
                  </tr>
                </thead>
                <tbody>
                  {events.map((ev) => (
                    <tr key={ev.id} className="border-t border-border">
                      <td className="p-3">{compName(ev.competition_id)}</td>
                      <td className="p-3 text-muted-foreground">
                        {new Date(ev.start_time).toLocaleString()}
                      </td>
                      <td className="p-3">
                        {ev.away_name} @ {ev.home_name}
                      </td>
                      <td className="p-3 tabular-nums">
                        {ev.away_score ?? "—"} – {ev.home_score ?? "—"}
                      </td>
                      <td className="p-3">
                        <select
                          aria-label="Event status"
                          value={ev.status}
                          onChange={(e) => updateEvent(ev.id, { status: e.target.value })}
                          className="min-h-[44px] rounded-lg border border-border bg-background px-2 text-xs"
                        >
                          {STATUSES.map((s) => (
                            <option key={s} value={s}>
                              {s}
                            </option>
                          ))}
                        </select>
                      </td>
                      <td className="p-3 text-xs text-muted-foreground">
                        {ev.source} · {ev.provider}
                      </td>
                      <td className="p-3 text-xs text-muted-foreground">
                        {new Date(ev.last_updated_at).toLocaleTimeString()}
                      </td>
                      <td className="p-3">
                        <label className="flex items-center gap-2 min-h-[44px]">
                          <input
                            type="checkbox"
                            checked={ev.published}
                            onChange={(e) => updateEvent(ev.id, { published: e.target.checked })}
                            className="w-4 h-4"
                          />
                        </label>
                      </td>
                      <td className="p-3">
                        <button
                          onClick={() => removeEvent(ev.id)}
                          className="min-h-[44px] px-3 text-xs font-semibold text-destructive"
                        >
                          Delete
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </section>
      </div>
    </AdminLayout>
  );
};

export default AdminScores;
